import { vec2, vec3, vec4, mat4, quat } from 'gl-matrix';
import { Subject } from 'rxjs';
import { eulerDegToQuat } from './PlacementComposer';

/**
 * Quaternion-native transform. Internal storage is `[T, R(quat), S]`; the
 * matrix is rebuilt via `mat4.fromRotationTranslationScale` on every change.
 *
 * The legacy `rotationX/Y/Z` accessors stay (the engine's "rotate by 90°"
 * ergonomic was the original reason for Euler), but they are now derived live
 * from the stored quat — XYZ-intrinsic decomposition with γ-collapse at
 * gimbal lock (±~89.94°). Callers that need lossless rotation reads should
 * use {@link getRotationQuat}; lossless writes should use
 * {@link setRotationQuat}.
 *
 * Why this matters: under the old representation `_rotation` was a vec3 of
 * Euler degrees rebuilt from on every position/scale setter. A
 * `setRotationQuat` followed by `position = ...` would silently lose the
 * rotation through a gimbal-lock decomposition; the new representation
 * stores the canonical quat so subsequent setters cannot drift the
 * orientation.
 */
export class TRS {
  public changed: Subject<TRS>;

  private changeInProgress: boolean;
  private _position: vec3;
  private _matrix: mat4;
  private _rotation: quat;
  private _scale: vec3;

  public get position(): vec3 | number[] {
    return this._position;
  }

  public set position(position: vec3 | number[]) {
    this._position = IndexedArrayAccess.createVec3(position, () => this.changeImpl());
    this.changeImpl();
  }

  public get matrix(): mat4 {
    return this._matrix;
  }

  public get rotationX(): number {
    return decomposeEulerXYZDeg(this._rotation)[0];
  }

  public set rotationX(rotationInDegrees: number) {
    const e = decomposeEulerXYZDeg(this._rotation);
    eulerDegToQuat(rotationInDegrees, e[1], e[2], this._rotation);
    this.changeImpl();
  }

  public get rotationY(): number {
    return decomposeEulerXYZDeg(this._rotation)[1];
  }

  public set rotationY(rotationInDegrees: number) {
    const e = decomposeEulerXYZDeg(this._rotation);
    eulerDegToQuat(e[0], rotationInDegrees, e[2], this._rotation);
    this.changeImpl();
  }

  public get rotationZ(): number {
    return decomposeEulerXYZDeg(this._rotation)[2];
  }

  public set rotationZ(rotationInDegrees: number) {
    const e = decomposeEulerXYZDeg(this._rotation);
    eulerDegToQuat(e[0], e[1], rotationInDegrees, this._rotation);
    this.changeImpl();
  }

  public get scale(): number {
    return this._scale[0];
  }

  public get x(): number {
    return this._position[0];
  }

  public set x(value: number) {
    this._position[0] = value;
  }

  public get y(): number {
    return this._position[1];
  }

  public set y(value: number) {
    this._position[1] = value;
  }

  public get z(): number {
    return this._position[2];
  }

  public set z(value: number) {
    this._position[2] = value;
  }

  public set scale(scale: number) {
    this.changeInProgress = true;
    this._scale[0] = scale;
    this._scale[1] = scale;
    this._scale[2] = scale;
    this.changeInProgress = false;
    this.changeImpl();
  }

  public setRotation(x: number, y: number, z: number): void {
    eulerDegToQuat(x, y, z, this._rotation);
    this.changeImpl();
  }

  /**
   * Set rotation from a quaternion. The quat is stored verbatim — round-trip
   * is lossless and a subsequent position/scale setter will not drift the
   * orientation through a gimbal-lock decomposition.
   */
  public setRotationQuat(rotation: quat): void {
    quat.copy(this._rotation, rotation);
    this.changeImpl();
  }

  /**
   * Lossless rotation read. Prefer this over `rotationX/Y/Z` when the caller
   * needs the exact stored orientation (e.g. gizmo composition, scene
   * persistence) — the Euler accessors collapse γ to 0 at gimbal lock.
   */
  public getRotationQuat(out?: quat): quat {
    const result = out ?? quat.create();
    return quat.copy(result, this._rotation);
  }

  constructor(position: vec3 | number[]) {
    this.changeInProgress = false;
    this.changed = new Subject();
    this._matrix = mat4.create();
    this._position = IndexedArrayAccess.createVec3(position, () => this.changeImpl());
    this._rotation = quat.create();
    this._scale = IndexedArrayAccess.createVec3(vec3.fromValues(1, 1, 1), () => this.changeImpl());
  }

  private changeImpl(): void {
    if (this.changeInProgress) {
      return;
    }

    mat4.fromRotationTranslationScale(
      this._matrix,
      this._rotation,
      this._position,
      this._scale,
    );
    this.changed.next(this);
  }
}

/**
 * XYZ-intrinsic Euler decomposition of a quaternion, returning degrees.
 * Equivalent to recovering (α, β, γ) from `R = Rx(α) · Ry(β) · Rz(γ)`.
 * At gimbal lock (|sinβ| ≥ 0.9999995, i.e. β ≈ ±90°), γ is arbitrarily
 * collapsed into α. Lossless reads should go through `getRotationQuat`.
 */
function decomposeEulerXYZDeg(q: quat): [number, number, number] {
  const qx = q[0], qy = q[1], qz = q[2], qw = q[3];
  const sinBeta = Math.max(-1, Math.min(1, 2 * (qw * qy + qx * qz)));
  const beta = Math.asin(sinBeta);
  let alpha: number;
  let gamma: number;
  if (Math.abs(sinBeta) < 0.9999995) {
    alpha = Math.atan2(2 * (qw * qx - qy * qz), 1 - 2 * (qx * qx + qy * qy));
    gamma = Math.atan2(2 * (qw * qz - qx * qy), 1 - 2 * (qy * qy + qz * qz));
  } else {
    alpha = Math.atan2(2 * (qx * qy + qw * qz), 1 - 2 * (qy * qy + qz * qz));
    gamma = 0;
  }
  const radToDeg = 180 / Math.PI;
  return [alpha * radToDeg, beta * radToDeg, gamma * radToDeg];
}

class IndexedArrayAccess {
  private constructor(
    public length: number,
    private data: number[],
    private callback: (index: number, oldValue: number, newValue: number) => void,
  ) {
    if (!data || data.length != length) {
      throw new Error(`Data array must be defined and contain ${length} elements`);
    }
  }

  public static createVec2(
    data: vec2 | number[],
    callback: (index: number, oldValue: number, newValue: number) => void,
  ): vec2 {
    return new IndexedArrayAccess(2, data as number[], callback) as any;
  }

  public static createVec3(
    data: vec3 | number[],
    callback: (index: number, oldValue: number, newValue: number) => void,
  ): vec3 {
    return new IndexedArrayAccess(3, data as number[], callback) as any;
  }

  public static createVec4(
    data: vec4 | number[],
    callback: (index: number, oldValue: number, newValue: number) => void,
  ): vec4 {
    return new IndexedArrayAccess(4, data as number[], callback) as any;
  }

  public get 0(): number {
    return this.data[0];
  }

  public set 0(value: number) {
    const oldValue = this.data[0];
    this.data[0] = value;

    this.callback(0, oldValue, value);
  }

  public get 1(): number {
    return this.data[1];
  }

  public set 1(value: number) {
    const oldValue = this.data[1];
    this.data[1] = value;

    this.callback(1, oldValue, value);
  }

  public get 2(): number {
    return this.data[2];
  }

  public set 2(value: number) {
    const oldValue = this.data[2];
    this.data[2] = value;

    this.callback(2, oldValue, value);
  }

  public get 3(): number {
    return this.data[3];
  }

  public set 3(value: number) {
    const oldValue = this.data[3];
    this.data[3] = value;

    this.callback(3, oldValue, value);
  }

  [Symbol.iterator](): IterableIterator<number> {
    return this.data[Symbol.iterator]();
  }
}
